import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card, Button, Spinner, Badge } from "@/components/ui/Primitives";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import { ShieldCheck, User, Save, CheckCircle2, XCircle, Trash2 } from "lucide-react";

const PERMS = [
  // Compras & NFs
  { k: "can_create_invoice", label: "Criar NF de entrada", cat: "Compras" },
  { k: "can_edit_invoice", label: "Editar NF / Precificar", cat: "Compras" },
  { k: "can_delete_invoice", label: "Excluir NF", cat: "Compras" },
  { k: "can_do_beneficiamento", label: "Realizar beneficiamento (produzir pallets)", cat: "Compras" },
  { k: "can_manage_suppliers", label: "Gerenciar fornecedores", cat: "Compras" },
  { k: "can_manage_sacaria", label: "Gerenciar estoque de sacarias", cat: "Compras" },
  // Vendas & Expedição
  { k: "can_create_shipment", label: "Criar saída / bipar expedição", cat: "Vendas" },
  { k: "can_delete_shipment", label: "Excluir/cancelar saída", cat: "Vendas" },
  { k: "can_manage_clients", label: "Gerenciar clientes", cat: "Vendas" },
  // Faturamento
  { k: "can_issue_nfe", label: "Emitir NF-e (Focus NF-e)", cat: "Faturamento" },
  // Financeiro
  { k: "can_access_financial", label: "Contas a pagar/receber, fluxo de caixa, recorrentes", cat: "Financeiro" },
  { k: "can_access_reports", label: "Relatórios (DRE, P&L, KPIs, fechamento semanal)", cat: "Financeiro" },
  // RH
  { k: "can_manage_employees", label: "RH — Funcionários e Folha de Pagamento", cat: "RH" },
  // Admin
  { k: "can_access_admin", label: "Configurações fiscais e da empresa", cat: "Administração" },
  { k: "can_access_settings", label: "Gerenciar usuários e permissões (esta tela)", cat: "Administração", admin_only: true },
];

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [editPerms, setEditPerms] = useState({});

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/auth/users"); setUsers(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === "admin" || user?.permissions?.can_access_settings) load(); else setLoading(false); }, [user]);

  const togglePerm = (uid, key) => {
    const current = editPerms[uid] ?? users.find((u) => u.id === uid)?.permissions ?? {};
    setEditPerms({ ...editPerms, [uid]: { ...current, [key]: !current[key] } });
  };

  const save = async (uid) => {
    setBusy(uid); setErr("");
    try {
      const target = editPerms[uid] ?? users.find((u) => u.id === uid)?.permissions ?? {};
      await api.put(`/auth/users/${uid}/permissions`, { permissions: target });
      await load();
      setEditPerms((s) => { const { [uid]: _drop, ...rest } = s; return rest; });
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(null); }
  };

  const toggleActive = async (uid, active) => {
    setBusy(uid); setErr("");
    try {
      await api.put(`/auth/users/${uid}/active`, { active });
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(null); }
  };

  const deleteUser = async (uid, email) => {
    if (!window.confirm(`Excluir o usuário ${email}?\nEsta ação não pode ser desfeita.`)) return;
    setBusy(uid); setErr("");
    try {
      await api.delete(`/auth/users/${uid}`);
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(null); }
  };

  const isAdmin = user?.role === "admin";
  const canAccessSettings = isAdmin || !!user?.permissions?.can_access_settings;

  if (!canAccessSettings) {
    return (
      <Layout title="Configurações" subtitle="Acesso restrito">
        <Card className="p-8 text-center">
          <ShieldCheck size={36} className="mx-auto text-ink-300 mb-3" />
          <h3 className="font-heading text-lg font-bold text-ink-900">Acesso administrativo necessário</h3>
          <p className="text-sm text-ink-500 mt-1">Solicite ao administrador a permissão &quot;Gerenciar usuários e permissões&quot;.</p>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Configurações" subtitle="Usuários e permissões granulares">
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : (
        <div className="space-y-6">
          {users.map((u) => {
            const perms = editPerms[u.id] ?? u.permissions ?? {};
            const dirty = !!editPerms[u.id];
            return (
              <Card key={u.id} className="p-6" data-testid={`user-card-${u.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center">
                      <User size={20} />
                    </div>
                    <div>
                      <div className="font-semibold text-ink-900">{u.name || u.email}</div>
                      <div className="text-xs text-ink-500 font-mono">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.role !== "admin" && (
                      <Badge tone={u.active ? "success" : "warn"}>
                        {u.active ? "ativo" : "aguardando liberação"}
                      </Badge>
                    )}
                    <Badge tone={u.role === "admin" ? "success" : "neutral"}>{u.role}</Badge>
                  </div>
                </div>
                {u.role !== "admin" && (
                  <>
                    {/* Botões de ativação/exclusão */}
                    <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-ink-200">
                      {u.active ? (
                        <Button
                          variant="secondary"
                          onClick={() => toggleActive(u.id, false)}
                          disabled={busy === u.id}
                          data-testid={`deactivate-${u.id}`}
                        >
                          <XCircle size={14} /> Desativar acesso
                        </Button>
                      ) : (
                        <Button
                          onClick={() => toggleActive(u.id, true)}
                          disabled={busy === u.id}
                          data-testid={`activate-${u.id}`}
                        >
                          <CheckCircle2 size={14} /> Liberar acesso
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          variant="danger"
                          onClick={() => deleteUser(u.id, u.email)}
                          disabled={busy === u.id}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 size={14} /> Excluir usuário
                        </Button>
                      )}
                    </div>
                    <div className="space-y-4">
                      {Array.from(new Set(PERMS.map((p) => p.cat))).map((cat) => (
                        <div key={cat}>
                          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-brand-500 mb-2">{cat}</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {PERMS.filter((p) => p.cat === cat).map((p) => {
                              const lockedForNonAdmin = p.admin_only && !isAdmin;
                              return (
                                <label
                                  key={p.k}
                                  className={`flex items-start gap-2 p-2.5 rounded-md border transition-colors ${perms[p.k] ? "border-brand-500 bg-emerald-50" : "border-ink-200"} ${lockedForNonAdmin ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-stone-50"}`}
                                  title={lockedForNonAdmin ? "Apenas o admin pode conceder esta permissão" : ""}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!perms[p.k]}
                                    onChange={() => togglePerm(u.id, p.k)}
                                    disabled={lockedForNonAdmin}
                                    className="accent-brand-500 mt-0.5"
                                    data-testid={`perm-${u.id}-${p.k}`}
                                  />
                                  <span className="text-sm">{p.label}{lockedForNonAdmin && <span className="ml-1 text-[10px] text-ink-400">(admin)</span>}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end mt-4">
                      <Button onClick={() => save(u.id)} disabled={!dirty || busy === u.id} data-testid={`save-perms-${u.id}`}>
                        {busy === u.id ? <Spinner size={14} /> : <><Save size={14} /> Salvar permissões</>}
                      </Button>
                    </div>
                  </>
                )}
                {u.role === "admin" && (
                  <p className="text-sm text-ink-500">Administradores possuem acesso total a todas as funções.</p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
